// src/hooks/useIncidentWorkspace.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { subscribeToIncidentEvents } from "../services/incidentEventService";
import { subscribeToFindings } from "../services/findingService";

/**
 * All live incident data for the workspace, in one place.
 *
 * Pulling these subscriptions out of the page fixes two problems the inline
 * version had: the files listener depended on the whole `problem` object and so
 * tore down and re-subscribed on every unrelated problem update, and the
 * messages listener depended on chat-open state for the same reason.
 */

const EMPTY = [];

const byCreatedAtAsc = (a, b) =>
  (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);

const byCreatedAtDesc = (a, b) =>
  (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);

/** Access outcome for the current viewer. */
export const WORKSPACE_STATUS = {
  LOADING: "loading",
  READY: "ready",
  MISSING: "missing",
  DENIED: "denied",
  UNASSIGNED: "unassigned",
  ERROR: "error",
};

function useSubcollection(problemId, name, compare, attempt) {
  const [items, setItems] = useState(EMPTY);

  useEffect(() => {
    if (!problemId) {
      setItems(EMPTY);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, "problems", problemId, name),
      (snapshot) => {
        const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows.sort(compare));
      },
      (error) => {
        console.error(`Workspace ${name} subscription error:`, error);
        setItems(EMPTY);
      }
    );

    return unsubscribe;
    // `compare` is a module-level function and therefore stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId, name, attempt]);

  return items;
}

/**
 * Derives a displayable evidence entry from the base64 attachment captured at
 * intake, so the customer's original screenshot sits alongside uploaded files
 * instead of being invisible in the workspace.
 */
function buildSubmissionAttachment(problem) {
  if (!problem?.attachment) return null;

  const match = problem.attachment.match(/^data:(image|video)\/(\w+);/);
  const filename = match
    ? `submitted-${match[1]}.${match[2]}`
    : "submitted-attachment";

  return {
    id: "submission-attachment",
    filename,
    downloadURL: problem.attachment,
    size: 0,
    uploaderId: problem.clientId,
    uploaderName: problem.clientName || problem.clientEmail || "Customer",
    createdAt: problem.createdAt,
    isSubmissionAttachment: true,
    storagePath: null,
  };
}

export function useIncidentWorkspace(problemId, currentUser) {
  const [problem, setProblem] = useState(null);
  const [status, setStatus] = useState(WORKSPACE_STATUS.LOADING);
  // Bumped by retry() to force every subscription in this hook to re-attach.
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const viewerId = currentUser?.uid || null;

  useEffect(() => {
    if (!problemId || !viewerId) return undefined;

    setStatus(WORKSPACE_STATUS.LOADING);

    const unsubscribe = onSnapshot(
      doc(db, "problems", problemId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setProblem(null);
          setStatus(WORKSPACE_STATUS.MISSING);
          return;
        }

        const data = { id: snapshot.id, ...snapshot.data() };
        const isParticipant =
          data.clientId === viewerId || data.helperId === viewerId;

        if (!isParticipant) {
          setProblem(null);
          setStatus(WORKSPACE_STATUS.DENIED);
          return;
        }

        setProblem(data);
        setStatus(
          data.clientId && data.helperId
            ? WORKSPACE_STATUS.READY
            : WORKSPACE_STATUS.UNASSIGNED
        );
      },
      (error) => {
        console.error("Workspace problem subscription error:", error);
        setStatus(WORKSPACE_STATUS.ERROR);
      }
    );

    return unsubscribe;
  }, [problemId, viewerId, attempt]);

  const messages = useSubcollection(problemId, "messages", byCreatedAtAsc, attempt);
  const uploads = useSubcollection(problemId, "files", byCreatedAtDesc, attempt);
  const updates = useSubcollection(problemId, "updates", byCreatedAtDesc, attempt);
  const notes = useSubcollection(problemId, "notes", byCreatedAtDesc, attempt);

  const [events, setEvents] = useState(EMPTY);
  useEffect(() => {
    if (!problemId) {
      setEvents(EMPTY);
      return undefined;
    }
    return subscribeToIncidentEvents(problemId, setEvents);
  }, [problemId, attempt]);

  const [findings, setFindings] = useState(EMPTY);
  const [findingsError, setFindingsError] = useState(null);
  useEffect(() => {
    if (!problemId) {
      setFindings(EMPTY);
      return undefined;
    }
    setFindingsError(null);
    return subscribeToFindings(problemId, setFindings, setFindingsError);
  }, [problemId, attempt]);

  const files = useMemo(() => {
    const submission = buildSubmissionAttachment(problem);
    return submission ? [submission, ...uploads] : uploads;
  }, [problem, uploads]);

  const unreadCount = useMemo(() => {
    if (!viewerId) return 0;
    return messages.filter(
      (m) =>
        m.senderId &&
        m.senderId !== viewerId &&
        m.type !== "system" &&
        !(Array.isArray(m.readBy) && m.readBy.includes(viewerId))
    ).length;
  }, [messages, viewerId]);

  return {
    problem,
    status,
    messages,
    files,
    updates,
    notes,
    events,
    findings,
    findingsError,
    unreadCount,
    retry,
  };
}
